<template>
  <teleport to="body">
    <div class="pdf-signature-backdrop" @click.self="$emit('close')">
      <div class="modal-dialog modal-dialog-centered modal-xl">
        <div class="modal-content bg-white rounded-3 shadow-lg pdf-signature-modal">
          <div class="modal-header border-bottom-0 cute-header shadow-lg">
            <div>
              <p class="modal-title text-white text-xxs mb-1">Admin signature required</p>
              <p class="text-white text-xxs mb-0">
                Add the signature for {{ entity_name || "this PDF report" }} before generation.
              </p>
            </div>
            <button
              type="button"
              class="modal-btn-close btn-close"
              aria-label="Close"
              @click="$emit('close')"
            ></button>
          </div>

          <div class="modal-body overflow-hidden">
            <div class="row g-3 h-100">
              <div class="col-12 col-lg-7 d-flex flex-column gap-3">
                <div class="card border rounded-lg shadow-sm p-3">
                  <div class="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <div>
                      <p class="fw-bold text-dark text-xxs mb-1">Draw signature</p>
                      <p class="text-muted text-xxs mb-0">
                        Use your mouse, trackpad, or finger.
                      </p>
                    </div>
                    <button
                      class="btn btn-light btn-sm rounded-small text-xxs shadow-sm"
                      type="button"
                      @click="clearSignature"
                    >
                      Clear
                    </button>
                  </div>
                  <canvas
                    ref="signatureCanvas"
                    class="pdf-signature-canvas rounded-small"
                    height="360"
                    width="1200"
                    @pointerdown="startDrawing"
                    @pointermove="draw"
                    @pointerup="stopDrawing"
                    @pointerleave="stopDrawing"
                  ></canvas>
                </div>
              </div>

              <div class="col-12 col-lg-5 d-flex flex-column gap-3">
                <div class="card border rounded-lg shadow-sm p-3">
                  <p class="fw-bold text-dark text-xxs mb-1">Or upload signature image</p>
                  <p class="text-muted text-xxs mb-3">
                    PNG, JPG, or SVG.
                  </p>
                  <input
                    class="form-control text-xxs rounded-small shadow-sm"
                    type="file"
                    accept="image/*"
                    @change="handleFileSelection"
                  />
                </div>

                <div class="card border rounded-lg shadow-sm p-3 flex-grow-1">
                  <p class="fw-bold text-dark text-xxs mb-1">Preview</p>
                  <div class="pdf-signature-preview rounded-small">
                    <img
                      v-if="signatureDataUrl"
                      :src="signatureDataUrl"
                      alt="Signature preview"
                    />
                    <p v-else class="text-muted text-xxs mb-0">
                      Your signature will appear here once drawn or uploaded.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 bg-white pdf-signature-footer">
            <button
              class="btn btn-light btn-sm rounded-small text-xxs shadow-sm"
              type="button"
              @click="$emit('close')"
            >
              Cancel
            </button>
            <button
              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
              :disabled="!signatureDataUrl"
              type="button"
              @click="confirmSignature"
            >
              Use Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: "PDFReportSignatureModal",
  props: {
    entity_name: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "confirm"],
  data: () => ({
    drawing: false,
    signatureDataUrl: "",
  }),
  mounted() {
    this.setupCanvas();
  },
  methods: {
    setupCanvas() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) {
        return;
      }
      const context = canvas.getContext("2d");
      context.lineCap = "round";
      context.lineJoin = "round";
      context.lineWidth = 4;
      context.strokeStyle = "#111827";
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
    },
    getCanvasPoint(event) {
      const canvas = this.$refs.signatureCanvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * canvas.width,
        y: ((event.clientY - rect.top) / rect.height) * canvas.height,
      };
    },
    startDrawing(event) {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) {
        return;
      }
      if (event.pointerType) {
        canvas.setPointerCapture(event.pointerId);
      }
      this.drawing = true;
      const context = canvas.getContext("2d");
      const point = this.getCanvasPoint(event);
      context.beginPath();
      context.moveTo(point.x, point.y);
      this.signatureDataUrl = "";
    },
    draw(event) {
      if (!this.drawing) {
        return;
      }
      const canvas = this.$refs.signatureCanvas;
      const context = canvas.getContext("2d");
      const point = this.getCanvasPoint(event);
      context.lineTo(point.x, point.y);
      context.stroke();
    },
    stopDrawing(event) {
      if (!this.drawing) {
        return;
      }
      const canvas = this.$refs.signatureCanvas;
      const context = canvas.getContext("2d");
      context.stroke();
      context.closePath();
      this.drawing = false;
      if (event?.pointerType) {
        canvas.releasePointerCapture(event.pointerId);
      }
      this.signatureDataUrl = canvas.toDataURL("image/png");
    },
    clearSignature() {
      const canvas = this.$refs.signatureCanvas;
      if (!canvas) {
        return;
      }
      const context = canvas.getContext("2d");
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      this.signatureDataUrl = "";
    },
    handleFileSelection(event) {
      const signatureFile = event.target.files?.[0] || null;
      event.target.value = "";
      if (!signatureFile) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.signatureDataUrl = String(reader.result || "");
      };
      reader.readAsDataURL(signatureFile);
    },
    confirmSignature() {
      if (!this.signatureDataUrl) {
        return;
      }
      this.$emit("confirm", { image_data_url: this.signatureDataUrl });
    },
  },
};
</script>

<style scoped>
.pdf-signature-backdrop {
  align-items: center;
  background: rgba(15, 23, 42, 0.48);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 0.75rem;
  position: fixed;
  z-index: 1090;
}

.pdf-signature-modal {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 1.5rem);
  overflow: hidden;
}

.pdf-signature-canvas {
  background: #ffffff;
  border: 1px dashed rgba(15, 23, 42, 0.24);
  touch-action: none;
  width: 100%;
  height: 13rem;
}

.pdf-signature-preview {
  align-items: center;
  background: #f8f9fa;
  border: 1px dashed rgba(15, 23, 42, 0.16);
  display: flex;
  justify-content: center;
  min-height: 10rem;
  padding: 1rem;
}

.pdf-signature-preview img {
  max-height: 8rem;
  max-width: 100%;
  object-fit: contain;
}

.pdf-signature-footer {
  bottom: 0;
  position: sticky;
  z-index: 2;
}
</style>
